import { useEffect, useRef, useState } from 'react';
import '@components/Battery/SiteUnitCommandsPopup/SiteUnitCommandsPopup.scoped.scss';
import Button from '@components/_elements/Button/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import storeConnector from '@store/storeConnector';
import FormattedAlias from './Translations/FormattedAlias';
import useAlias from '@src/hooks/useAlias';

const styles = {
  cmdCheckboxLabel: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '0 15px 15px',
    cursor: 'pointer',
    margin: 0,
  },
  cmdCheckbox: {
    padding: 0,
    marginRight: '5px',
  },
};

const ConfirmationPopup = ({
  actions,
  popupProps,
  siteMeta,
  commands,
  setCommands,
  unitTable,
  UnitName,
  itemsSelected,
  setItemsSelected,
  multiselectItems,
  setMultiselectItems,
  type,
  confirmationPopupChecklist,
  setConfirmationPopupChecklist,
  setShowConfirmationPopup,
  converterIdxStr,
  setOverInput,
  unitCommands,
  converterCommands,
  setModeFunc,
  setPrimaryModeFunc,
  setSecondaryModeFunc,
  pendingIsSecondaryRef,
  pendingModeValueRef,
  lockedControllers,
}) => {
  const [unitReactive, setUnitReactive] = useState(0);
  const [unitReal, setUnitReal] = useState(0);
  const [afterSelectConfirmDlg, setAfterSelectConfirmDlg] = useState(false);
  const alias = useAlias();
  const inputRef = useRef();

  const titleMultiselectDlg = (commandName) => {
    // Check if this is converter/BMS multiselect
    const isConverterMultiselect =
      converterIdxStr.current === 'DCDC' || converterIdxStr.current === 'BMS';

    const itemType = isConverterMultiselect
      ? converterIdxStr.current === 'DCDC'
        ? alias('converters')
        : alias('BMS units')
      : popupProps.popupType === 'site'
        ? alias('units')
        : alias('racks');

    const command = commandName ? commandName : commands[0];

    return alias('Choose {itemType} to execute command {command}.', {
      itemType,
      command,
    });
  };

  useEffect(() => {
    setItemsSelected(
      itemsSelected.map((item) => ({
        ...item,
        isDisabledByControlLock: lockedControllers
          .map((device) => device.deviceId)
          .includes(item.unitSN),
      })),
    );
  }, [lockedControllers]);

  const buildSelectedItemsConfirmString = () => {
    const selectedItems = [];
    let count = 0;

    multiselectItems.forEach((r, i) => {
      if (
        itemsSelected[i].unitControllerSelected &&
        itemsSelected[i].isDisabledByControlLock === false
      ) {
        selectedItems.push(r);
        count++;
      }
    });

    // Check if this is converter/BMS multiselect
    const isConverterMultiselect =
      converterIdxStr.current === 'DCDC' || converterIdxStr.current === 'BMS';

    const itemType = isConverterMultiselect
      ? count > 1
        ? converterIdxStr.current === 'DCDC'
          ? alias('converters')
          : alias('BMS units')
        : converterIdxStr.current === 'DCDC'
          ? alias('converter')
          : alias('BMS unit')
      : popupProps.popupType === 'site'
        ? count > 1
          ? alias('units')
          : alias('unit')
        : count > 1
          ? alias('racks')
          : alias('rack');

    return alias(' for {itemType}: {items}', {
      itemType,
      items: selectedItems.join(', '),
    });
  };

  const submitPopup = () => {
    if (afterSelectConfirmDlg && confirmationPopupChecklist) {
      // Handle converter/BMS multiselect
      const isConverterMultiselect =
        converterIdxStr.current === 'DCDC' || converterIdxStr.current === 'BMS';

      if (isConverterMultiselect) {
        // Send command to each selected converter/BMS
        multiselectItems.forEach((item, index) => {
          if (
            itemsSelected[index].unitControllerSelected &&
            itemsSelected[index].isDisabledByControlLock === false
          ) {
            // item is like "DCDC01" or "BMS01"
            actions.sendCommand(commands, popupProps.sn, item);
          }
        });
      } else if (popupProps.popupType === 'site') {
        unitTable.forEach((unitRow) => {
          if (
            itemsSelected.find(
              (itemSelectedRow) =>
                itemSelectedRow.unitSN === unitRow.SN &&
                itemSelectedRow.unitControllerSelected === true &&
                itemSelectedRow.isDisabledByControlLock === false,
            )
          ) {
            const comms =
              commands[0] === 'Power::'
                ? [`${commands[0]}${unitReal}::${unitReactive}`]
                : commands;

            actions.sendCommand(comms, unitRow.SN);
          }
        });
      } else {
        actions.sendCommand(
          [
            `${commands[0]}::[${itemsSelected
              .map((el, i) =>
                el && el.isDisabledByControlLock !== false ? i : '',
              )
              .filter((e) => e !== '')
              .join(',')}]`,
          ],
          popupProps.sn,
          popupProps.isPV ? UnitName : null,
        );
      }

      setAfterSelectConfirmDlg(false);
      setConfirmationPopupChecklist(false);
      setShowConfirmationPopup(false);
      setCommands([]);
      setMultiselectItems([]);
      setItemsSelected([]);
    } else {
      if (confirmationPopupChecklist) {
        return;
      }

      let converterOrUnitName = converterIdxStr.current
        ? converterIdxStr.current
        : popupProps.isPV
          ? UnitName
          : null;

      actions.sendCommand(commands, popupProps.sn, converterOrUnitName);

      converterIdxStr.current = null;
    }

    if (type === 'mode') {
      const command = commands[0];
      const commandSegments = command.split('::');
      //  command may have a 3rd segment - ::Persist suffix
      //  or the siteMeta custom command string may have nested "::" strings
      //  So, split and grab the second last for mode value
      //  element in the array (if it exists)
      const nextVal =
        commandSegments[commandSegments.length - 1] === 'Persist'
          ? commandSegments[commandSegments.length - 2]
          : commandSegments[commandSegments.length - 1];
      const apply = pendingIsSecondaryRef?.current
        ? setSecondaryModeFunc
        : setPrimaryModeFunc;
      if (typeof apply === 'function') {
        apply(nextVal);
      } else if (typeof setModeFunc === 'function') {
        // Fallback to legacy single-callback if provided
        setModeFunc(nextVal);
      }
      if (pendingModeValueRef) {
        pendingModeValueRef.current = null;
      }
      if (pendingIsSecondaryRef) {
        pendingIsSecondaryRef.current = false;
      }
    }
  };

  const onEnter = (key) => {
    if (key === 0) {
      inputRef.current.focus();
    }
  };

  const siteCommandsIsArray = Array.isArray(siteMeta?.Site_Commands);
  const unitCommandsIsArray = Array.isArray(unitCommands);
  const converterCommandsIsArray = Array.isArray(converterCommands);

  let allCommandsList;

  if (siteCommandsIsArray) {
    allCommandsList = [
      ...(siteMeta?.Site_Commands || []),
      ...(unitCommandsIsArray ? unitCommands : []),
      ...(converterCommandsIsArray ? converterCommands : []),
    ].flat();
  }

  const commandName = allCommandsList?.find(
    (el) => el.Command === commands[0],
  )?.Name;

  return (
    <div className='cover-container cover-container-confirm'>
      {confirmationPopupChecklist && !afterSelectConfirmDlg ? (
        <>
          <div
            className='control-popup-container'
            style={{ padding: '20px 30px' }}
          >
            <div className='title-line' style={{ textAlign: 'center' }}>
              {titleMultiselectDlg(commandName)}
            </div>

            {multiselectItems.length !== 0 ? (
              <div className='rack-choose-list'>
                <div id='first-cell'>
                  <Button
                    onClick={() => {
                      setItemsSelected(
                        itemsSelected.map((item) => ({
                          ...item,
                          unitControllerSelected: !item.isDisabledByControlLock,
                        })),
                      );
                    }}
                  >
                    <FormattedAlias id={'Select All'} />
                  </Button>

                  <Button
                    onClick={() => {
                      setItemsSelected(
                        itemsSelected.map((item) => ({
                          ...item,
                          unitControllerSelected: false,
                        })),
                      );
                    }}
                  >
                    <FormattedAlias id={'Unselect All'} />
                  </Button>
                </div>

                {multiselectItems.map((device, index) => {
                  return (
                    <FormControlLabel
                      key={index}
                      style={styles.cmdCheckboxLabel}
                      disabled={itemsSelected[index].isDisabledByControlLock}
                      control={
                        <Checkbox
                          style={styles.cmdCheckbox}
                          size='small'
                          onClick={() => {
                            const newArr = [...itemsSelected];
                            newArr[index].unitControllerSelected =
                              !newArr[index].unitControllerSelected;
                            setItemsSelected(newArr);
                          }}
                          checked={
                            itemsSelected[index].isDisabledByControlLock ===
                              false &&
                            itemsSelected[index].unitControllerSelected === true
                          }
                        />
                      }
                      label={
                        converterIdxStr.current === 'DCDC' ||
                        converterIdxStr.current === 'BMS'
                          ? device // Just show "DCDC01", "BMS02", etc. without prefix
                          : `${
                              popupProps.popupType === 'site'
                                ? alias('Unit')
                                : alias('Rack')
                            } ${device}`
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                {alias('No {itemType} online', {
                  itemType:
                    converterIdxStr.current === 'DCDC'
                      ? alias('converters')
                      : popupProps.popupType === 'site'
                        ? alias('units')
                        : alias('racks'),
                })}
              </div>
            )}

            {commands[0] === 'Power::' && (
              <div className='popup-row' style={{ alignSelf: 'center' }}>
                <div className='bold'>
                  <FormattedAlias id={'Power'} />
                </div>
                <div className='popup-table'>
                  <div className='popup-table-col'>
                    <div>
                      <FormattedAlias id={'Real (P):'} />
                    </div>
                    <div className='range-lable' />
                    <div>
                      <FormattedAlias id={'Reactive (Q):'} />
                    </div>
                    <div className='range-lable' />
                  </div>
                  <div className='popup-table-col'>
                    <div>
                      <input
                        onKeyDown={(e) => {
                          if (e.keyCode === 13) {
                            onEnter(0);
                            e.preventDefault();
                          }
                        }}
                        className={'comand-popup-inp '}
                        type='number'
                        step='0.1'
                        value={unitReal}
                        onMouseEnter={() => setOverInput(true)}
                        onMouseLeave={() => setOverInput(false)}
                        onChange={(e) => setUnitReal(e.target.value)}
                      />
                    </div>
                    <div className='range-lable' />
                    <div>
                      <input
                        ref={inputRef}
                        className={'comand-popup-inp '}
                        type='number'
                        step='0.1'
                        value={unitReactive}
                        onMouseEnter={() => setOverInput(true)}
                        onMouseLeave={() => setOverInput(false)}
                        onChange={(e) => setUnitReactive(e.target.value)}
                      />
                    </div>
                    <div className='range-lable' />
                  </div>
                  <div className='popup-table-col'>
                    <div>
                      <FormattedAlias id={'kW'} />
                    </div>
                    <div className='range-lable' />
                    <div>
                      <FormattedAlias id={'kvar'} />
                    </div>
                    <div className='range-lable' />
                  </div>
                </div>
              </div>
            )}
            <div className='popup-row commands-popup-control'>
              <Button
                size='m'
                disabled={
                  !itemsSelected.some(
                    (item) => item.unitControllerSelected === true,
                  )
                }
                onClick={() => {
                  submitPopup();
                  setAfterSelectConfirmDlg(true);
                }}
              >
                <FormattedAlias id={'OK'} />
              </Button>
              <Button
                size='m'
                onClick={() => {
                  setShowConfirmationPopup(false);
                  setCommands([]);
                  setMultiselectItems([]);
                  setItemsSelected([]);
                  if (pendingModeValueRef) {
                    pendingModeValueRef.current = null;
                  }
                  if (pendingIsSecondaryRef) {
                    pendingIsSecondaryRef.current = false;
                  }
                }}
              >
                <FormattedAlias id={'CANCEL'} />
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className='control-popup-container small-popup'>
            <div className='popup-row centre'>
              {
                <>
                  <div>
                    <FormattedAlias
                      id={'Are you sure you want to execute the command:'}
                    />
                    &nbsp;
                    <span className='bold'>
                      {commandName ? commandName : commands[0]}{' '}
                    </span>
                  </div>
                  <div className='normal'>
                    <span>{`${afterSelectConfirmDlg ? buildSelectedItemsConfirmString() : ''}`}</span>
                  </div>
                </>
              }
            </div>
            <div className='popup-row commands-popup-control'>
              {
                <Button
                  size='m'
                  onClick={() => {
                    submitPopup();
                    setShowConfirmationPopup(false);
                  }}
                >
                  <FormattedAlias id={'OK'} />
                </Button>
              }
              <Button
                size='m'
                onClick={() => {
                  setShowConfirmationPopup(false);
                  setCommands([]);
                  setAfterSelectConfirmDlg(false);
                  setMultiselectItems([]);
                  setItemsSelected([]);
                  converterIdxStr.current = null;
                  if (pendingModeValueRef) {
                    pendingModeValueRef.current = null;
                  }
                  if (pendingIsSecondaryRef) {
                    pendingIsSecondaryRef.current = false;
                  }
                }}
              >
                <FormattedAlias id={'CANCEL'} />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default storeConnector(ConfirmationPopup, {
  service: ['popupProps'],
  config: ['siteMeta'],
  controlsLock: ['lockedControllers'],
});
